import { ArrowLeft, ShoppingCart } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { getProductDetail } from "../api/products";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useCart } from "../context/CartContext";
import { formatCurrency } from "../utils/format";

export function ProductDetailPage() {
  const { id } = useParams();
  const { state } = useLocation();
  const { addToCart, cartItems, loading, removeItem, updateQuantity } = useCart();
  const [apiProduct, setApiProduct] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [error, setError] = useState("");
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    async function loadProduct() {
      try {
        setDetailLoading(true);
        setError("");
        console.log("productId", id);
        const response = await getProductDetail(id, "", controller.signal);
        console.log("detail API response", response);
        console.log("image field value", response?.imageUrl || response?.images?.[0] || "");
        setApiProduct(response);
      } catch (requestError) {
        if (requestError.name !== "CanceledError" && requestError.code !== "ERR_CANCELED") {
          setError("Product details could not be loaded.");
        }
      } finally {
        setDetailLoading(false);
      }
    }

    loadProduct();

    return () => controller.abort();
  }, [id]);

  const product = useMemo(() => {
    const stateProduct = state?.product || {};
    const source = apiProduct || stateProduct;
    const imageCandidates = [
      apiProduct?.imageUrl,
      ...(apiProduct?.images || []),
      stateProduct?.imageUrl,
      ...(stateProduct?.images || []),
    ].filter(Boolean);
    const uniqueImageCandidates = [...new Set(imageCandidates)];
    const dynamicUom = source.dynamicUOM || [];
    const activeUom = dynamicUom.length > 1 ? source.selectedUOM || dynamicUom[0] : {
      uomId: 2,
      uomDesc: "UNIT",
      unitPerCarton: source.unitPerCarton || 1,
      price: source.price || 0,
    };
    return {
      productId: source.productId || id,
      productName: source.productName || "Product details unavailable",
      productCode: source.productCode || "",
      price: Number(activeUom.price || source.price || 0),
      imageUrl: uniqueImageCandidates[imageIndex] || "",
      imageCandidates: uniqueImageCandidates,
      dynamicUOM: source.dynamicUOM || [],
      selectedUOM: {
        uomId: activeUom.uomId || source.uomId || 0,
        uomDesc: activeUom.uomDesc || source.uomDesc || "",
        unitPerCarton: activeUom.unitPerCarton || source.unitPerCarton || 1,
        price: Number(activeUom.price || source.price || 0),
      },
      description:
        source.description ||
        source.description2 ||
        source.description3 ||
        "Detailed description will appear here when available.",
    };
  }, [apiProduct, id, imageIndex, state?.product]);

  const cartItem = useMemo(() => {
    const productIdKey = String(product.productId || "");
    const productUomId = Number(product.selectedUOM?.uomId || 0);

    return (
      cartItems.find((item) => {
        const itemProductId = String(item.productId ?? item.ProductID ?? item.ProductId ?? "");
        const itemUomId = Number(item.uomId ?? item.UOMID ?? 0);

        if (itemProductId !== productIdKey) {
          return false;
        }

        return !itemUomId || !productUomId || itemUomId === productUomId;
      }) || null
    );
  }, [cartItems, product.productId, product.selectedUOM?.uomId]);

  useEffect(() => {
    setImageIndex(0);
  }, [apiProduct, state?.product]);

  useEffect(() => {
    console.log("final image URL", product.imageUrl || "");
    console.log("image fallback candidates", product.imageCandidates || []);
  }, [product.imageCandidates, product.imageUrl]);

  function handleImageError() {
    console.warn("Product detail image failed to load", product.imageUrl);
    if (imageIndex < product.imageCandidates.length - 1) {
      setImageIndex((current) => current + 1);
    }
  }

  async function handleAddToCart() {
    try {
      await addToCart(product, 1);
    } catch (error) {
      // CartContext already exposes cart errors to the active cart UI.
    }
  }

  async function handleQuantityChange(nextQuantity) {
    if (!cartItem) {
      return;
    }

    try {
      if (nextQuantity <= 0) {
        await removeItem(cartItem);
        return;
      }

      await updateQuantity(cartItem, nextQuantity);
    } catch (error) {
      // CartContext already exposes cart errors to the active cart UI.
    }
  }

  return (
    <div className="space-y-6">
      <Link className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground" to="/products">
        <ArrowLeft className="h-4 w-4" />
        Back to products
      </Link>

      <Card className="overflow-hidden border-border bg-card shadow-sm">
        <CardContent className="grid gap-8 p-0 lg:grid-cols-[1fr_0.95fr]">
          <div className="aspect-[4/3] bg-secondary lg:aspect-auto lg:min-h-[520px]">
            {product.imageUrl ? (
              <img
                alt={product.productName}
                className="h-full w-full object-cover"
                onError={handleImageError}
                src={product.imageUrl}
              />
            ) : (
              <div className="flex h-full min-h-[320px] items-center justify-center text-sm font-semibold text-muted-foreground">
                No Image Available
              </div>
            )}
          </div>

          <div className="flex flex-col p-6 sm:p-8">
            {detailLoading ? (
              <p className="mb-4 text-sm font-semibold text-muted-foreground">Loading product...</p>
            ) : null}
            {error ? (
              <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Pickup item
            </p>
            <h1 className="mt-3 font-display text-3xl font-semibold leading-tight sm:text-5xl">
              {product.productName}
            </h1>
            {product.productCode ? (
              <p className="mt-3 text-sm text-muted-foreground">Code: {product.productCode}</p>
            ) : null}
            <p className="mt-5 text-3xl font-semibold text-primary">{formatCurrency(product.price)}</p>
            <p className="mt-3 text-sm text-muted-foreground">
              UOM: {product.selectedUOM.uomDesc || "UNIT"}
            </p>
            <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground">
              {product.description}
            </p>

            {cartItem ? (
              <div className="mt-8 flex h-12 w-full items-center justify-between rounded-xl border border-primary bg-primary/5 px-3 text-primary transition sm:max-w-sm">
                <button
                  className="rounded-lg px-4 py-1 text-xl font-bold transition hover:bg-primary hover:text-primary-foreground"
                  disabled={loading}
                  onClick={() => handleQuantityChange(cartItem.qty - 1)}
                  type="button"
                >
                  -
                </button>
                <span className="min-w-12 text-center text-base font-bold">{cartItem.qty}</span>
                <button
                  className="rounded-lg px-4 py-1 text-xl font-bold transition hover:bg-primary hover:text-primary-foreground"
                  disabled={loading}
                  onClick={() => handleQuantityChange(cartItem.qty + 1)}
                  type="button"
                >
                  +
                </button>
              </div>
            ) : (
              <Button
                className="mt-8 w-full sm:max-w-sm"
                disabled={loading || !product.productId}
                onClick={handleAddToCart}
                size="lg"
                type="button"
              >
                <ShoppingCart className="h-4 w-4" />
                + Add to Cart
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
